import { useCallback, useRef, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { getAgentFileUrl, getAgentStreamUrl } from '../../thunks/agent_harness_thunks';
import { applyActionReview } from '../../ai_agent/utils/OscarActionReviews';
import {
	AgentErrorInfo,
	classifyAgentError,
	humanizeAgentError,
} from '../../utils/AgentErrorUtils';
import {
	AgentAction,
	AgentActionOutcome,
	AgentApprovalsResponse,
	AgentApprovalsResult,
	AgentConversationInfo,
	AgentMessageFeedback,
	AgentModelsResponse,
	AgentPageContext,
	AgentPendingApproval,
	AgentSessionInfo,
	AgentStreamEvent,
	AgentTranscriptEntry,
	AgentTurnStatusOutcome,
	AgentUploadResponse,
} from '../../types/agentHarness';

type ToolEntry = Extract<AgentTranscriptEntry, { kind: 'tool' }>;

export interface AgentChatActions {
	exchangeLogin: () => Promise<AgentSessionInfo>;
	fetchModels: () => Promise<AgentModelsResponse>;
	/** `GET /api/me` — who the harness believes the live session belongs to. */
	fetchIdentity: () => Promise<AgentSessionInfo>;
	createConversation: (model?: string, thinkingLevel?: string) => Promise<AgentConversationInfo>;
	sendMessage: (
		conversationId: string,
		messageId: string,
		text: string,
		context?: AgentPageContext
	) => Promise<void>;
	cancelTurn: (conversationId: string) => Promise<void>;
	setConversationModel: (conversationId: string, model: string) => Promise<void>;
	setThinkingLevel: (conversationId: string, thinkingLevel: string) => Promise<void>;
	resolveApproval: (approvalId: string, approved: boolean) => Promise<void>;
	/**
	 * `POST /api/approvals` — one answer for several approvals. Optional: without
	 * it, or against a harness that answers 404/405, `resolveApprovals` sends one
	 * `resolveApproval` per id instead.
	 */
	resolveApprovals?: (ids: string[], approved: boolean) => Promise<AgentApprovalsResponse>;
	/** Rates one answer. Optional: a surface without the thumbs can leave it out. */
	sendFeedback?: (
		conversationId: string,
		messageId: string,
		feedback: AgentMessageFeedback
	) => Promise<void>;
	/** `GET /api/actions` — this login's writes whose result was lost. */
	fetchActions?: () => Promise<AgentAction[]>;
	/** `GET /api/actions/:id` — one record in any state. */
	fetchAction?: (actionId: string) => Promise<AgentAction>;
	/** Records the user's verification of one of those; never runs the write. */
	reconcileAction?: (
		actionId: string,
		outcome: AgentActionOutcome,
		evidence: string
	) => Promise<AgentAction>;
	uploadFile: (file: File) => Promise<AgentUploadResponse>;
	/**
	 * `GET /api/conversations/:id`. Only `openConversation` needs it, so a
	 * surface that never switches conversations can leave it out.
	 */
	fetchConversation?: (conversationId: string) => Promise<AgentConversationInfo>;
}

export type AgentChatStatus = 'connecting' | 'ready' | 'reconnecting' | 'error';

/** `agentJson` puts the HTTP status on the error it throws. */
const isAuthFailure = (error: any): boolean =>
	!!error && (error.status === 401 || error.status === 403);

/**
 * True only when the server has told us the session cannot be used again.
 *
 * The distinction matters on resume: giving up on a stored conversation costs
 * the user their transcript, so it is worth doing only for a failure that
 * proves reopening it is impossible. A network error proves nothing.
 */
const isSessionUnusable = (error: any): boolean =>
	isAuthFailure(error) || (!!error && error.status === 404);

/**
 * Marks an error whose cause is already on screen in the connection strip, so
 * the caller's own catch does not report the same failure a second time.
 */
const markSurfaced = (error: any): any => {
	if (error && typeof error === 'object') {
		error.surfaced = true;
	}
	return error;
};

/**
 * How long to wait for a stream to say `hello` before calling it a failure.
 *
 * Generous: the harness sends `hello` in its first write, so this is only ever
 * reached when the stream genuinely never opened. Shorter, and a slow network
 * reads as a dead connection.
 */
const HELLO_TIMEOUT_MS = 15000;

/** Why a picked chat would not open, in words about *that* chat. */
const describeOpenFailure = (error: any): string => {
	const status = error ? error.status : 0;
	if (status === 409) {
		// The harness's own copy says "continue it there", and there is often no
		// "there" left to go to: the holder is an earlier session of this person.
		return 'That chat is still open in another session. Try again in a few minutes, or start a new chat.';
	}
	if (status === 404) {
		return 'That chat could not be found. It may have been deleted.';
	}
	const raw = error instanceof Error ? error.message : '';
	return raw ? humanizeAgentError(raw) : 'That chat could not be opened. Try again.';
};

const mintMessageId = (): string => {
	return 'u-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

/** An older harness has no batch route: `POST /api/approvals` is 404 or 405 there. */
/** What a rejected harness call carries: `agentJson` puts the HTTP status on it. */
type AgentCallError = { status?: number } | null | undefined;

const isMissingRoute = (error: AgentCallError): boolean =>
	!!error && (error.status === 404 || error.status === 405);

/** `POST /api/approvals/:id` answers 404 for an approval it no longer holds. */
const isGone = (error: AgentCallError): boolean => !!error && error.status === 404;

/**
 * How long after `hello` a batch it saw counts as complete. A step's approvals
 * register within milliseconds of each other, so this only has to outlast a
 * `hello` that landed while one was still arriving: marking at once let "Approve
 * all" cover the members shown and leave a late one behind.
 */
export const BATCH_SETTLE_MS = 1500;

let noticeCounter = 0;
const mintNoticeId = (): string => {
	noticeCounter += 1;
	return 'notice-' + noticeCounter;
};

/**
 * All chat state for one agent conversation: the login exchange, the SSE
 * stream, the transcript, approvals, and uploads. Transcript entries are keyed
 * by the ids the harness mints, so replay after a dropped stream (EventSource
 * reconnects with Last-Event-ID automatically) updates rows in place instead
 * of duplicating them.
 */
/** A message whose network call failed, kept so it can be sent again. */
export interface AgentUnsentMessage {
	/** The id it was first sent under — reused so a retry updates that row. */
	id: string;
	text: string;
	/**
	 * The page context it was sent with. A retry must resend it unchanged: the harness hashes text
	 * and context under the message id, and a different or missing context is a 409.
	 */
	context?: AgentPageContext;
	/** Humanized reason, or empty when the connection strip already says why. */
	reason: string;
}

/**
 * Unsent messages by id, in the order they were sent.
 *
 * Keyed rather than a single slot so a failure stays attached to *its* message.
 * With one slot, sending a second message after a failure quietly dropped the
 * first one's marker and left it looking delivered when the agent never saw it.
 */
export type AgentUnsentMessages = { [messageId: string]: AgentUnsentMessage };

export interface AgentChatOptions {
	/** Resume this conversation instead of creating one, restoring its transcript. */
	conversationId?: string;
	/** Called whenever the live conversation id changes, for the caller to persist. */
	onConversationId?: (conversationId: string) => void;
	/**
	 * Whether a live harness session may be reused rather than replaced.
	 *
	 * Given what the harness reports for the session it already holds. Answering
	 * false only costs a login exchange; answering true wrongly means running as
	 * whoever the old session belonged to, so the caller should refuse on any
	 * doubt. Supplied by the caller because the comparison needs OpenWrench
	 * identity — account, role, workspace — that this hook has no business
	 * knowing.
	 */
	canReuseSession?: (harnessEmail: string, harnessRole: string) => boolean;
	/** Called after a fresh exchange, so the caller can record whose session it is. */
	onSessionEstablished?: () => void;
	/**
	 * Called on every `idle`. A resumed conversation replays one per earlier
	 * turn, so this can fire in a burst.
	 */
	onTurnEnd?: () => void;
	/**
	 * Called on every `hello`. A stream only opens once the harness session is
	 * known to be this person's — reused after `/api/me` agreed, or freshly
	 * exchanged — so this is the earliest safe point to read anything that is
	 * scoped to the session's identity.
	 */
	onStreamReady?: () => void;
	/**
	 * A chat the person picked could not be opened. The open conversation is
	 * untouched, so the reason belongs beside the pick — the caller's toast —
	 * not in this transcript, where it read as the open chat failing and piled
	 * up a copy per click. Omitted, it goes into the transcript as before.
	 */
	onSwitchError?: (message: string) => void;
}

export const useAgentChat = (actions: AgentChatActions, options?: AgentChatOptions) => {
	const [status, setStatus] = useState<AgentChatStatus>('connecting');
	const [errorInfo, setErrorInfo] = useState<AgentErrorInfo | null>(null);
	const [busy, setBusy] = useState(false);
	// When the current turn began, in epoch ms; 0 while idle. Kept here rather
	// than derived from `busy` because a turn is not one continuous busy stretch:
	// an agentic turn finishes several assistant messages before it is over.
	const [turnStartedAt, setTurnStartedAt] = useState(0);
	const [entries, setEntries] = useState<AgentTranscriptEntry[]>([]);
	// Read from async callbacks that need the cards on screen now, not at creation.
	const entriesRef = useRef(entries);
	entriesRef.current = entries;
	const [conversation, setConversation] = useState<AgentConversationInfo | null>(null);
	const [modelsInfo, setModelsInfo] = useState<AgentModelsResponse | null>(null);
	const [pendingUploads, setPendingUploads] = useState<AgentUploadResponse[]>([]);
	const [uploading, setUploading] = useState(false);
	const [unsentMessages, setUnsentMessages] = useState<AgentUnsentMessages>({});
	/**
	 * The turn the stream is currently on: the last `user_text` it delivered.
	 * Notices for a turn's end are keyed by it, so a full replay — every in-place
	 * reopen sends one, with no Last-Event-ID — updates them instead of stacking
	 * a second copy.
	 */
	const streamTurnRef = useRef('');
	/** The `turn_status` this turn ended on, so `idle` does not repeat its text. */
	const turnStatusRef = useRef<{ text: string; outcome: AgentTurnStatusOutcome } | null>(null);
	/** Pending `BATCH_SETTLE_MS` timer, so a newer `hello` or a reset replaces it. */
	const batchSettleTimerRef = useRef(0);

	const eventSourceRef = useRef<EventSource | null>(null);
	// The re-auth path runs from a stream callback that outlives the render it
	// was created in, so it needs the live conversation rather than the one
	// captured at the time.
	const conversationRef = useRef<AgentConversationInfo | null>(null);
	const recoveringRef = useRef(false);
	const loginRef = useRef<Promise<AgentSessionInfo> | null>(null);
	const unmountedRef = useRef(false);
	/**
	 * The conversation `connect` should try to reopen.
	 *
	 * Retry used to call `connect('')`, which always started a fresh chat. That
	 * turned a moment offline into a lost transcript: the stored conversation was
	 * still perfectly resumable, and Try again was what threw it away.
	 */
	const resumeIdRef = useRef(options && options.conversationId ? options.conversationId : '');
	// A conversation switch is under way: from the click until the next
	// conversation's `hello`, or until the switch gives up. The ref answers
	// synchronously (one switch at a time, nothing sent mid-switch); the state is
	// what the composer and the rail render from.
	const switchingRef = useRef(false);
	const [switching, setSwitching] = useState(false);

	/** Resolves when the stream currently opening says `hello`. */
	const helloWaiterRef = useRef<{ resolve: () => void; reject: (error: any) => void } | null>(null);

	/** Settles the waiter one way and clears it, so a later stream gets its own. */
	const settleHello = useCallback((error?: any) => {
		const waiter = helloWaiterRef.current;
		helloWaiterRef.current = null;
		if (!waiter) {
			return;
		}
		if (error) {
			waiter.reject(error);
		} else {
			waiter.resolve();
		}
	}, []);

	/**
	 * A promise for the next `hello`, armed *before* the stream is opened.
	 *
	 * `openStream` only constructs an `EventSource`; nothing about that says the
	 * connection was accepted. Treating it as readiness let the composer take a
	 * message into a conversation the client could not hear back from, and a
	 * refused stream then replaced the conversation and took the message with it.
	 *
	 * Bounded, because the opposite failure is worse: a panel stuck in
	 * "connecting" for ever, with no error and nothing to retry.
	 */
	const awaitHello = useCallback((): Promise<void> => {
		settleHello(new Error('Superseded by a newer connection'));
		return new Promise<void>((resolve, reject) => {
			const timer = window.setTimeout(() => {
				settleHello(new Error('The assistant did not finish connecting.'));
			}, HELLO_TIMEOUT_MS);
			const done = (settleError?: any) => {
				window.clearTimeout(timer);
				if (settleError) {
					reject(settleError);
				} else {
					resolve();
				}
			};
			helloWaiterRef.current = { resolve: () => done(), reject: (error: any) => done(error) };
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settleHello]);

	/**
	 * `awaitHello` for a caller that may throw before it awaits the result. The
	 * waiter rejects on its timeout, or when a retry arms the next one; with
	 * nobody awaiting it that is an unhandled rejection. A handler attached at
	 * arm time keeps it quiet, and an `await` on the returned promise still sees
	 * the rejection.
	 */
	const armHello = useCallback((): Promise<void> => {
		const waiter = awaitHello();
		waiter.catch(() => undefined);
		return waiter;
	}, [awaitHello]);

	/**
	 * Exchanges a login, or joins the one already in flight.
	 *
	 * An expiring session tends to be noticed twice at once — the stream emits
	 * `auth_error` at about the moment the next call 401s — and every exchange
	 * burns one of the harness's MAX_SESSIONS (10 by default). Coalescing means
	 * one lapse costs one slot however many callers trip over it.
	 */
	const exchangeLoginOnce = useCallback((): Promise<AgentSessionInfo> => {
		if (!loginRef.current) {
			const clear = () => {
				loginRef.current = null;
			};
			loginRef.current = actions.exchangeLogin().then(
				(session) => {
					clear();
					return session;
				},
				(loginError) => {
					clear();
					throw loginError;
				}
			);
		}
		return loginRef.current;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actions.exchangeLogin]);

	/** Starts the turn clock, or leaves a running one alone. */
	const startTurnClock = useCallback(() => {
		setTurnStartedAt((current) => current || Date.now());
	}, []);

	const endTurn = useCallback(() => {
		setBusy(false);
		setTurnStartedAt(0);
	}, []);

	const appendNotice = useCallback((severity: 'info' | 'error', text: string) => {
		setEntries((current) => [...current, { kind: 'notice', id: mintNoticeId(), severity, text }]);
	}, []);

	/**
	 * The one place a rejected call turns into a transcript notice. Skips errors
	 * the connection strip already owns, and humanizes the rest — the harness
	 * relays provider payloads verbatim, so a raw `error` field is not
	 * necessarily something a person can read.
	 */
	const appendErrorNotice = useCallback(
		(error: any, fallback: string) => {
			if (error && error.surfaced) {
				return;
			}
			const raw = error instanceof Error ? error.message : '';
			appendNotice('error', raw ? humanizeAgentError(raw) : fallback);
		},
		[appendNotice]
	);

	/** A notice with a stable id: replaced in place when the same id arrives again. */
	const upsertNotice = useCallback((id: string, severity: 'info' | 'error', text: string) => {
		setEntries((current) => {
			const nextEntry: AgentTranscriptEntry = { kind: 'notice', id, severity, text };
			const index = current.findIndex(
				(existing) => existing.kind === 'notice' && existing.id === id
			);
			if (index === -1) {
				return [...current, nextEntry];
			}
			const next = current.slice();
			next[index] = nextEntry;
			return next;
		});
	}, []);

	const upsertApproval = useCallback((approval: AgentPendingApproval) => {
		setEntries((current) => {
			const index = current.findIndex(
				(existing) => existing.kind === 'approval' && existing.id === approval.id
			);
			if (index !== -1) {
				return current;
			}
			return [
				...current,
				{
					kind: 'approval',
					id: approval.id,
					toolCallId: approval.toolCallId,
					toolName: approval.toolName,
					args: approval.args,
					destructive: approval.destructive,
					resolved: false,
					approved: false,
					rationale: approval.rationale,
					labels: approval.labels,
					actionId: approval.actionId,
					createdAt: approval.createdAt,
					batchId: approval.batchId,
					batchSize: approval.batchSize,
				},
			];
		});
	}, []);

	/**
	 * Settles cards the harness no longer holds. Whatever became of them —
	 * answered in another tab, expired, ended with their turn — happened out of
	 * sight, so they read "no longer pending" rather than failed or rejected. A
	 * later `approval_resolved` for one still overwrites this with its reason.
	 */
	const settleMissingApprovals = useCallback((isMissing: (approvalId: string) => boolean) => {
		setEntries((current) => {
			let changed = false;
			const next = current.map((existing) => {
				if (existing.kind !== 'approval' || existing.resolved || !isMissing(existing.id)) {
					return existing;
				}
				changed = true;
				return { ...existing, resolved: true, approved: false, reason: 'missing' as const };
			});
			return changed ? next : current;
		});
	}, []);

	/**
	 * A write whose result was lost pauses every later call of that tool for this
	 * login until someone records what happened, so it gets a review card.
	 */
	const upsertActionReview = useCallback((action: AgentAction) => {
		setEntries((current) => applyActionReview(current, action));
	}, []);

	/**
	 * Re-reads one action, so a card answered in another chat or tab — whose
	 * stream this one never hears — retires instead of offering a stale choice.
	 * Resolves true when the record arrived.
	 */
	const refreshActionReview = useCallback(
		(actionId: string): Promise<boolean> => {
			if (!actions.fetchAction) {
				return Promise.resolve(false);
			}
			return actions
				.fetchAction(actionId)
				.then((action) => {
					if (!unmountedRef.current && action) {
						upsertActionReview(action);
					}
					return !!action;
				})
				.catch(() => false);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.fetchAction, upsertActionReview]
	);

	/**
	 * The stream only replays this conversation's events, but the pause covers
	 * the whole login, so a write lost in another chat (or before a deploy) has
	 * to be fetched. Best effort: the chat works without it. An open card missing
	 * from the list was answered elsewhere, so it is re-read to retire it.
	 */
	const loadActionReviews = useCallback(() => {
		if (!actions.fetchActions) {
			return;
		}
		actions
			.fetchActions()
			.then((unresolved) => {
				if (unmountedRef.current) {
					return;
				}
				unresolved.forEach(upsertActionReview);
				const stillOpen = new Set(unresolved.map((action) => action.id));
				entriesRef.current.forEach((entry) => {
					if (
						entry.kind === 'action_review' &&
						entry.state === 'outcome_unknown' &&
						!stillOpen.has(entry.id)
					) {
						void refreshActionReview(entry.id);
					}
				});
			})
			.catch(() => undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actions.fetchActions, upsertActionReview, refreshActionReview]);

	const handleStreamEvent = useCallback(
		(event: AgentStreamEvent) => {
			switch (event.type) {
				case 'hello': {
					// The stream is live. This is what "ready" means — not that a REST
					// call succeeded — and it is the only signal that arrives together
					// with the replayed transcript.
					settleHello();
					setStatus('ready');
					setErrorInfo(null);
					// `endTurn` rather than `setBusy(false)`: this is the authoritative
					// state, and leaving `turnStartedAt` from the previous turn behind
					// meant the *next* turn inherited its start time and announced
					// itself as slow the moment it began.
					if (event.busy) {
						setBusy(true);
						startTurnClock();
					} else {
						endTurn();
					}
					setConversation((current) =>
						current
							? {
									...current,
									model: event.model,
									thinkingLevel: event.thinkingLevel,
									toolset: event.toolset,
							  }
							: current
					);
					// `hello` is the live list, sent after the replay. A card it does not
					// name was settled while the stream was down — a replay gap can drop its
					// `approval_resolved` — and left open it would offer, and "Approve all"
					// would post, a decision for an approval that no longer exists.
					const stillPending = new Set(event.pending.map((approval) => approval.id));
					settleMissingApprovals((approvalId) => !stillPending.has(approvalId));
					event.pending.forEach(upsertApproval);
					// Every batch member that still exists is now on screen — replayed or
					// just listed — so the batches seen here cannot grow, bar a member
					// still registering this instant. Without this, a batch that lost
					// members to a replay gap waited for them for good, with its bulk
					// buttons disabled. They settle after `BATCH_SETTLE_MS`, so a late
					// member joins first; a later step's batch is never touched.
					setEntries((current) =>
						current.some(
							(existing) =>
								existing.kind === 'approval' && existing.batchId && !existing.batchSeenAtHello
						)
							? current.map((existing) =>
									existing.kind === 'approval' && existing.batchId && !existing.batchSeenAtHello
										? { ...existing, batchSeenAtHello: true }
										: existing
							  )
							: current
					);
					window.clearTimeout(batchSettleTimerRef.current);
					batchSettleTimerRef.current = window.setTimeout(() => {
						batchSettleTimerRef.current = 0;
						if (unmountedRef.current) {
							return;
						}
						setEntries((current) => {
							const seen = new Set<string>();
							current.forEach((existing) => {
								if (existing.kind === 'approval' && existing.batchId && existing.batchSeenAtHello) {
									seen.add(existing.batchId);
								}
							});
							const settles = (existing: AgentTranscriptEntry) =>
								existing.kind === 'approval' &&
								!!existing.batchId &&
								!existing.batchComplete &&
								seen.has(existing.batchId);
							return current.some(settles)
								? current.map((existing) =>
										settles(existing) ? { ...existing, batchComplete: true } : existing
								  )
								: current;
						});
					}, BATCH_SETTLE_MS);
					loadActionReviews();
					if (options && options.onStreamReady) {
						options.onStreamReady();
					}
					break;
				}
				case 'gap': {
					appendNotice('info', 'Some earlier messages could not be replayed after a reconnect.');
					break;
				}
				case 'user_text': {
					streamTurnRef.current = event.id;
					turnStatusRef.current = null;
					// The server has the message. That settles it even if our POST is
					// still in flight or its response was lost: without this, a dropped
					// response marked an accepted message "Not sent", so the reply
					// streamed in beside a Try again that would have run the turn twice.
					setUnsentMessages((current) => {
						if (!current[event.id]) {
							return current;
						}
						const next = { ...current };
						delete next[event.id];
						return next;
					});
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'user' && existing.id === event.id
						);
						if (index !== -1) {
							return current;
						}
						// Replay and other tabs bring the context back with the text.
						return [
							...current,
							{
								kind: 'user',
								id: event.id,
								text: event.text,
								...(event.context ? { context: event.context } : {}),
							},
						];
					});
					break;
				}
				case 'assistant_thinking':
				case 'assistant_text':
				case 'assistant_done': {
					// `idle` ends the turn — not this. An agentic turn completes several
					// assistant messages, each with its own `assistant_done`, before it is
					// actually over; clearing `busy` here blinked the indicator out and
					// restarted the elapsed timer at every step, so it could never reach a
					// minute no matter how long the turn ran.
					if (event.type !== 'assistant_done') {
						setBusy(true);
						startTurnClock();
					}
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'assistant' && existing.id === event.id
						);
						const previous =
							index !== -1
								? (current[index] as { text: string; thinking: string; superseded?: boolean })
								: { text: '', thinking: '' };
						// Assistant events are cumulative snapshots, not deltas: each one
						// replaces the field it carries and preserves the other.
						let text = previous.text;
						let thinking = previous.thinking;
						let done = false;
						if (event.type === 'assistant_thinking') {
							thinking = event.text;
						} else if (event.type === 'assistant_text') {
							text = event.text;
						} else {
							text = event.text;
							thinking = event.thinking;
							done = true;
						}
						const nextEntry: AgentTranscriptEntry = {
							kind: 'assistant',
							id: event.id,
							text,
							thinking,
							done,
							superseded: previous.superseded,
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'tool_start': {
					setBusy(true);
					startTurnClock();
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'tool' && existing.id === event.id
						);
						const nextEntry: AgentTranscriptEntry = {
							kind: 'tool',
							id: event.id,
							name: event.name,
							args: event.args,
							summary: '',
							isError: false,
							done: false,
							superseded: index !== -1 && (current[index] as ToolEntry).superseded,
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'tool_end': {
					// A failed write may be the pause itself — set by a write lost in
					// another chat, which this stream never hears about — or a fresh
					// unknown outcome. Either way the review list may have changed, and
					// this is also the retry for a load that failed on `hello`.
					if (event.isError) {
						loadActionReviews();
					}
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'tool' && existing.id === event.id
						);
						if (index === -1) {
							return [
								...current,
								{
									kind: 'tool',
									id: event.id,
									name: event.name,
									args: undefined,
									summary: event.summary,
									isError: event.isError,
									done: true,
								},
							];
						}
						const next = current.slice();
						const existing = next[index] as ToolEntry;
						next[index] = {
							kind: 'tool',
							id: event.id,
							name: event.name,
							args: existing.args,
							summary: event.summary,
							isError: event.isError,
							done: true,
							superseded: existing.superseded,
						};
						return next;
					});
					break;
				}
				case 'approval_required': {
					upsertApproval(event);
					break;
				}
				case 'action': {
					upsertActionReview(event.action);
					break;
				}
				case 'approval_resolved': {
					setEntries((current) =>
						current.map((existing) =>
							existing.kind === 'approval' && existing.id === event.id
								? {
										...existing,
										resolved: true,
										approved: event.approved,
										// Why it did not run: only `denied` was the person's choice.
										reason: event.approved ? undefined : event.reason,
								  }
								: existing
						)
					);
					break;
				}
				case 'file': {
					// Not every `file` event is a file for the user. The harness spills
					// any tool result over TOOL_RESULT_MAX_BYTES (64 KB) to
					// `tool-result-<uuid>.json` purely so the model can page it back with
					// read_tool_result, then broadcasts it on the same event as a real
					// export — so an ordinary work-order query produced a 97 KB download
					// card of escaped ToolResult envelope (OPE-5281).
					//
					// The two are told apart by the endpoint that serves them, which is a
					// distinction the harness already makes: deliberate output —
					// `save_file` and sandbox files — is served from `/api/outputs/`,
					// while `/api/tool-results/` is model plumbing with no user-facing
					// meaning. Keying on the route rather than the file name or extension
					// means an artifact cannot be mistaken for an export the user asked
					// for. The harness should stop announcing these at all; until it
					// does, dropping them here keeps them out of the transcript.
					if (event.url.indexOf('/api/tool-results/') === 0) {
						break;
					}
					setEntries((current) => {
						const index = current.findIndex(
							(existing) => existing.kind === 'file' && existing.id === event.id
						);
						const nextEntry: AgentTranscriptEntry = {
							kind: 'file',
							id: event.id,
							name: event.name,
							size: event.size,
							url: getAgentFileUrl(event.url),
						};
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'team_request': {
					// Keyed by tool-call id, like `file`: a replayed stream updates the
					// card it already showed instead of stacking a second one. Namespaced,
					// because the tool call's own activity row is keyed by the same id and
					// the two render as siblings — a shared React key lets React reuse the
					// wrong row.
					const entryId = `team-request:${event.id}`;
					setEntries((current) => {
						const nextEntry: AgentTranscriptEntry = {
							kind: 'team_request',
							id: entryId,
							requestKind: event.kind,
							request: event.request,
						};
						const index = current.findIndex(
							(existing) => existing.kind === 'team_request' && existing.id === entryId
						);
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'auth_error': {
					// The harness session lapsed, not the conversation — it is still
					// there, so re-exchange the login and reopen the stream on it
					// rather than telling the user to go and sign in.
					void reauthenticate();
					break;
				}
				case 'turn_status': {
					// The harness's own account of how the turn ended, counted from its
					// action journal. Keyed by request, so a replay updates the block.
					turnStatusRef.current = { text: event.text, outcome: event.outcome };
					const entryId = `${event.requestId}:status`;
					setEntries((current) => {
						const nextEntry: AgentTranscriptEntry = {
							kind: 'turn_status',
							id: entryId,
							requestId: event.requestId,
							outcome: event.outcome,
							text: event.text,
							writes: event.writes,
						};
						const index = current.findIndex(
							(existing) => existing.kind === 'turn_status' && existing.id === entryId
						);
						if (index === -1) {
							return [...current, nextEntry];
						}
						const next = current.slice();
						next[index] = nextEntry;
						return next;
					});
					break;
				}
				case 'idle': {
					endTurn();
					if (options && options.onTurnEnd) {
						options.onTurnEnd();
					}
					// `turn_status` precedes `idle`. For a stopped turn its text *is*
					// `idle.error`, and a silent turn's status already says so — the
					// status block has it, a footnote repeating it is noise.
					const status = turnStatusRef.current;
					turnStatusRef.current = null;
					const noticeId = `idle:${streamTurnRef.current || 'opening'}`;
					if (event.error) {
						if (!status || status.text !== event.error) {
							// Through the humanizer like every other failure: this is the
							// path that relays a provider's payload or SDK wording verbatim.
							upsertNotice(noticeId, 'error', humanizeAgentError(event.error));
						}
					} else if (event.silent && !(status && status.outcome === 'silent')) {
						upsertNotice(
							noticeId,
							'info',
							'The model finished without producing a reply — try again, or lower the reasoning level.'
						);
					}
					break;
				}
				case 'thinking_level': {
					setConversation((current) =>
						current ? { ...current, thinkingLevel: event.thinkingLevel } : current
					);
					break;
				}
				case 'model': {
					setConversation((current) => (current ? { ...current, model: event.model } : current));
					break;
				}
				case 'escalated': {
					// The retry starts the answer over. What the abandoned attempt showed
					// is marked so the transcript can fold it away as a first pass rather
					// than let it read as the reply.
					const discardedMessages = new Set(event.discarded ? event.discarded.messages : []);
					const discardedTools = new Set(event.discarded ? event.discarded.tools : []);
					if (discardedMessages.size || discardedTools.size) {
						setEntries((current) =>
							current.map((existing) =>
								(existing.kind === 'assistant' && discardedMessages.has(existing.id)) ||
								(existing.kind === 'tool' && discardedTools.has(existing.id))
									? { ...existing, superseded: true }
									: existing
							)
						);
					}
					break;
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			appendNotice,
			upsertNotice,
			upsertApproval,
			settleMissingApprovals,
			upsertActionReview,
			loadActionReviews,
			startTurnClock,
			endTurn,
			settleHello,
		]
	);

	const closeStream = useCallback(() => {
		if (eventSourceRef.current) {
			eventSourceRef.current.close();
			eventSourceRef.current = null;
		}
	}, []);

	const openStream = useCallback(
		(conversationId: string) => {
			closeStream();
			const source = new EventSource(getAgentStreamUrl(conversationId), {
				withCredentials: true,
			});
			source.onmessage = (messageEvent) => {
				let parsed: AgentStreamEvent | null = null;
				try {
					parsed = JSON.parse(messageEvent.data);
				} catch (parseError) {
					parsed = null;
				}
				if (parsed) {
					handleStreamEvent(parsed);
				}
			};
			source.onerror = () => {
				// Transient drops reconnect on their own with Last-Event-ID; a CLOSED
				// source means the server refused the stream — most likely the
				// harness restarted and this session is gone. Recover once, silently.
				if (source.readyState === EventSource.CLOSED && !unmountedRef.current) {
					// Anyone waiting on this stream's `hello` will never get it. Say so
					// now rather than making them sit out the timeout.
					settleHello(new Error('The assistant connection was refused.'));
					void recoverSession();
				}
			};
			eventSourceRef.current = source;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[closeStream, handleStreamEvent, settleHello]
	);

	/** What belongs to the conversation on screen, as opposed to the connection. */
	const resetTranscript = useCallback(() => {
		setEntries([]);
		window.clearTimeout(batchSettleTimerRef.current);
		batchSettleTimerRef.current = 0;
		streamTurnRef.current = '';
		turnStatusRef.current = null;
		setPendingUploads([]);
		// A message that failed in the previous conversation must not stay
		// retryable — `retry` would deliver it into this one.
		setUnsentMessages({});
		endTurn();
	}, [endTurn]);

	const startConversation = useCallback(
		async (model?: string, thinkingLevel?: string) => {
			const created = await actions.createConversation(model, thinkingLevel);
			// The panel can be closed, or expanded to fullscreen, while this request
			// is in flight. The mount cleanup has then already run and closed the
			// stream that existed at the time, so opening another one here left an
			// EventSource alive with nothing to close it — holding a harness
			// connection and dispatching events into an abandoned hook.
			if (unmountedRef.current) {
				return created;
			}
			setConversation(created);
			conversationRef.current = created;
			resumeIdRef.current = created.id;
			if (options && options.onConversationId) {
				options.onConversationId(created.id);
			}
			resetTranscript();
			openStream(created.id);
			return created;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.createConversation, openStream]
	);

	/**
	 * Reopen an existing conversation rather than creating one. The server
	 * replays its whole event log to a stream that sends no Last-Event-ID, and
	 * user messages are in that log, so the full transcript comes back. `hello`
	 * fills in the model and toolset, which is why they start empty here.
	 */
	const resumeConversation = useCallback(
		(conversationId: string) => {
			const resumed = { id: conversationId, model: '', thinkingLevel: '', toolset: '' };
			setConversation(resumed);
			conversationRef.current = resumed;
			resumeIdRef.current = conversationId;
			resetTranscript();
			openStream(conversationId);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[openStream]
	);

	/**
	 * Whether the current harness session can reopen `conversationId`.
	 *
	 * A new session is not a new conversation: the harness restores a chat it no
	 * longer holds from stored history, scoped to the session's own person and
	 * workspace. The read is what tells "gone" from "unreachable", because a
	 * refused EventSource carries no status. False only for the two answers that
	 * prove it cannot be reopened here — 404 (unknown, deleted, or not this
	 * person's) and 409 (open in another sign-in); anything else rejects, so the
	 * caller surfaces a retryable error and the conversation is not given up.
	 */
	const canReopen = useCallback(
		async (conversationId: string): Promise<boolean> => {
			if (!conversationId || !actions.fetchConversation) {
				return false;
			}
			try {
				await actions.fetchConversation(conversationId);
				return true;
			} catch (reopenError) {
				if (reopenError && (reopenError.status === 404 || reopenError.status === 409)) {
					return false;
				}
				throw reopenError;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.fetchConversation]
	);

	/**
	 * Re-exchange the login and carry on with the same conversation.
	 *
	 * Harness sessions are in-memory and expire on their own TTL (8h by default),
	 * so a 401 on a call that worked a minute ago usually means only the session
	 * lapsed. `agent_harness_thunks` has always documented this — *"Call it before
	 * anything else, and again on any 401"* — but nothing did it, so a lapsed
	 * session read as a dead assistant.
	 *
	 * A fresh exchange costs one of MAX_SESSIONS (10 by default), which is why
	 * this runs only on a real 401 and never speculatively.
	 */
	const reauthenticate = useCallback(async () => {
		if (recoveringRef.current || unmountedRef.current) {
			return;
		}
		recoveringRef.current = true;
		setStatus('reconnecting');
		try {
			await exchangeLoginOnce();
			if (unmountedRef.current) {
				return;
			}
			if (conversationRef.current) {
				// The reopened stream is what proves the new session can actually see
				// this conversation, so wait for its `hello` rather than assuming.
				const reopened = awaitHello();
				openStream(conversationRef.current.id);
				await reopened;
			}
			setStatus('ready');
			setErrorInfo(null);
		} catch (authError) {
			if (unmountedRef.current) {
				return;
			}
			setStatus('error');
			setErrorInfo(classifyAgentError(authError));
		} finally {
			recoveringRef.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exchangeLoginOnce, openStream, awaitHello]);

	/**
	 * Runs a harness call, and on a 401 re-exchanges the login and runs it once
	 * more. Anything else propagates untouched.
	 *
	 * If the second attempt fails too, the connection strip takes ownership of
	 * the message and the error is marked surfaced, so the caller's catch does
	 * not also drop a duplicate notice into the transcript.
	 */
	const withReauth = useCallback(
		async <T>(run: () => Promise<T>): Promise<T> => {
			try {
				return await run();
			} catch (firstError) {
				if (!isAuthFailure(firstError) || unmountedRef.current) {
					throw firstError;
				}
			}
			setStatus('reconnecting');
			try {
				await exchangeLoginOnce();
			} catch (loginError) {
				// No session at all — the assistant is down, not just this call.
				if (!unmountedRef.current) {
					setStatus('error');
					setErrorInfo(classifyAgentError(loginError));
				}
				throw markSurfaced(loginError);
			}
			try {
				const result = await run();
				if (!unmountedRef.current) {
					setStatus('ready');
					setErrorInfo(null);
				}
				return result;
			} catch (retryError) {
				if (isAuthFailure(retryError)) {
					// A brand-new session was refused too, so the OpenWrench token
					// behind it is dead and nothing here can fix that.
					if (!unmountedRef.current) {
						setStatus('error');
						setErrorInfo(classifyAgentError(retryError));
					}
					throw markSurfaced(retryError);
				}
				// The session is fine and this one call simply failed — keep the
				// assistant usable and let the caller put a notice in the transcript.
				if (!unmountedRef.current) {
					setStatus('ready');
				}
				throw retryError;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[exchangeLoginOnce]
	);

	const recoverSession = useCallback(async () => {
		if (recoveringRef.current || unmountedRef.current) {
			return;
		}
		recoveringRef.current = true;
		setStatus('reconnecting');
		try {
			await exchangeLoginOnce();
			// Same rule as `connect`: the replacement stream has to say `hello`
			// before this counts as recovered. Marking it ready on the strength of
			// a constructed EventSource reopened the composer onto a stream that
			// might itself have been refused — and the next recovery would then
			// clear whatever had been typed into it.
			//
			// The conversation comes back if it can (OPE-5443). This used to start
			// a fresh one on every refusal, on the theory that a restarted harness
			// had lost it — so a deploy or a lapsed session threw the transcript
			// away, and any unsent message with it, when the harness would have
			// restored the chat from history.
			const live = conversationRef.current;
			const targetId = live ? live.id : resumeIdRef.current;
			if (await canReopen(targetId)) {
				if (unmountedRef.current) {
					return;
				}
				const reopened = awaitHello();
				if (live) {
					// In place, as `reauthenticate` does: the replay reuses the ids of
					// the rows already on screen, and an unsent message stays retryable.
					openStream(targetId);
				} else {
					resumeConversation(targetId);
				}
				await reopened;
			} else {
				const recovered = awaitHello();
				await startConversation();
				await recovered;
				appendNotice('info', 'The assistant restarted, so this is a fresh conversation.');
			}
			setStatus('ready');
			setErrorInfo(null);
		} catch (recoveryError) {
			setStatus('error');
			setErrorInfo(classifyAgentError(recoveryError));
		} finally {
			recoveringRef.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		exchangeLoginOnce,
		canReopen,
		openStream,
		resumeConversation,
		startConversation,
		appendNotice,
		awaitHello,
	]);

	/**
	 * Bring the chat up: resume `resumeId` if one is given, otherwise exchange a
	 * login and start fresh. Separate from the mount effect so the connection
	 * strip's retry button can run it again — a boot failure used to be terminal
	 * for the lifetime of the panel.
	 */
	const connect = useCallback(
		async (resumeId: string): Promise<boolean> => {
			setStatus('connecting');
			setErrorInfo(null);
			try {
				// One probe for both paths, and it asks *who* rather than merely
				// whether. Each login exchange consumes one of MAX_SESSIONS (10 by
				// default), so reusing a good session matters — the cookie is shared
				// across tabs while the conversation id is not, and a second tab that
				// exchanged unconditionally replaced the cookie and stranded the first
				// tab's conversation in an abandoned session.
				//
				// But a working session is not necessarily *this person's* session.
				// The cookie has no Max-Age and the sign-out logout is best effort, so
				// it can outlive the account that created it; `/api/models` would
				// happily answer for the previous user and the new sign-in would then
				// read and write as them. `/api/me` is what makes reuse safe.
				let reusable = false;
				try {
					const harnessUser = await actions.fetchIdentity();
					reusable = !!(
						options &&
						options.canReuseSession &&
						options.canReuseSession(harnessUser.email, harnessUser.role)
					);
				} catch (probeError) {
					// Only a session the server has rejected is worth replacing. This
					// used to catch everything, so one dropped request — offline for a
					// second, a 500, a rate limit — started a fresh chat and stranded a
					// transcript that was still perfectly resumable. Anything else goes
					// to the boot handler, which surfaces a retryable error and leaves
					// `resumeIdRef` intact.
					if (!isSessionUnusable(probeError)) {
						throw probeError;
					}
				}
				if (unmountedRef.current) {
					return false;
				}

				if (!reusable) {
					// The old session goes; the stored conversation need not. It used to
					// be dropped here too, so coming back after the session lapsed (or
					// after another tab replaced it) opened an empty chat in place of the
					// one you left. The stored id is already bound to this person and
					// workspace, and the harness restores only its own person's history,
					// so it is tried against the new session below.
					await exchangeLoginOnce();
					if (options && options.onSessionEstablished) {
						options.onSessionEstablished();
					}
				}
				const models = await actions.fetchModels();
				if (unmountedRef.current) {
					return false;
				}
				setModelsInfo(models);

				if (resumeId && (reusable || (await canReopen(resumeId)))) {
					if (unmountedRef.current) {
						return false;
					}
					// Deliberately NOT ready yet. Going ready here flipped the panel
					// out of "connecting" before the stream had opened, so the empty
					// state rendered its greeting for one frame and the replayed
					// conversation then replaced it. `hello` sets ready instead, and
					// the harness sends it in the same write as the replay, so the
					// transcript and the status change land in one render.
					const resumed = awaitHello();
					const live = conversationRef.current;
					if (live && live.id === resumeId) {
						// Try again on the chat already on screen — after a recovery that
						// could not finish — reopens it in place. Rebuilding it cleared the
						// unsent messages before the stream had answered, so a retry that
						// failed again lost the one message the strip promised to send.
						openStream(resumeId);
					} else {
						resumeConversation(resumeId);
					}
					await resumed;
					return !unmountedRef.current;
				}

				resumeIdRef.current = '';
				// Armed before the stream is opened, and awaited after: returning true
				// on the strength of `openStream` alone said "connected" when all that
				// existed was a constructed EventSource, and `retry` took that as
				// licence to post a message into a conversation the client could not
				// hear back from.
				const connected = awaitHello();
				await startConversation();
				if (unmountedRef.current) {
					settleHello();
					await connected;
					return false;
				}
				await connected;
				return !unmountedRef.current;
			} catch (bootError) {
				if (unmountedRef.current) {
					return false;
				}
				setStatus('error');
				setErrorInfo(classifyAgentError(bootError));
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			exchangeLoginOnce,
			actions.fetchModels,
			actions.fetchIdentity,
			canReopen,
			openStream,
			resumeConversation,
			startConversation,
			awaitHello,
			settleHello,
		]
	);

	useMountEffect(() => {
		unmountedRef.current = false;
		void connect(options && options.conversationId ? options.conversationId : '');
		return () => {
			unmountedRef.current = true;
			window.clearTimeout(batchSettleTimerRef.current);
			closeStream();
		};
	});

	/**
	 * Puts one message on the wire under `messageId`.
	 *
	 * Split out from `sendMessage` so a retry can re-send the *same* message
	 * rather than composing a new one: the id is reused, so the row already in
	 * the transcript is the row that updates, and the stream's echo still
	 * de-duplicates against it.
	 *
	 * Reads the conversation from the ref because a retry may have just rebuilt
	 * it, and this callback would still be holding the previous render's value.
	 */
	const deliverMessage = useCallback(
		async (text: string, messageId: string, context?: AgentPageContext) => {
			const active = conversationRef.current;
			// Mid-switch the conversation on screen is being left: a message sent
			// now would start a turn nobody will see.
			if (!active || switchingRef.current) {
				return;
			}
			setUnsentMessages((current) => {
				if (!current[messageId]) {
					return current;
				}
				const next = { ...current };
				delete next[messageId];
				return next;
			});
			// Render immediately; the stream echoes it back under the same id, which
			// the user_text handler recognises instead of duplicating. On a retry the
			// row is usually already there, which is why this is an upsert.
			setEntries((current) =>
				current.some((existing) => existing.kind === 'user' && existing.id === messageId)
					? current
					: [...current, { kind: 'user', id: messageId, text, ...(context ? { context } : {}) }]
			);
			setBusy(true);
			setTurnStartedAt(Date.now());
			try {
				await withReauth(() => actions.sendMessage(active.id, messageId, text, context));
			} catch (sendError) {
				// A message that never left the browser is not a turn.
				endTurn();
				// No transcript notice here — the message keeps its own row with the
				// reason and a Try again on it, which is more use than a line of red
				// text under something the user has to retype.
				const unsent: AgentUnsentMessage = {
					id: messageId,
					// `classifyAgentError` rather than `humanizeAgentError`: its wording
					// assumes an action sits next to it, so it does not end with an
					// instruction the button is already giving.
					reason: sendError && sendError.surfaced ? '' : classifyAgentError(sendError).message,
					text,
					context,
				};
				setUnsentMessages((current) => ({ ...current, [messageId]: unsent }));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.sendMessage, withReauth, endTurn]
	);

	const sendMessage = useCallback(
		async (text: string, context?: AgentPageContext) => {
			if (!conversationRef.current) {
				return;
			}
			const trimmed = text.trim();
			const uploadMarkers = pendingUploads
				.map((upload) => '[Uploaded file: ' + upload.path + ']')
				.join('\n');
			const fullText = (trimmed + (uploadMarkers ? '\n' + uploadMarkers : '')).trim();
			if (!fullText) {
				return;
			}
			setPendingUploads([]);
			await deliverMessage(fullText, mintMessageId(), context);
		},
		[pendingUploads, deliverMessage]
	);

	/**
	 * The Try again behind both the connection strip and an unsent message.
	 *
	 * Reconnects first if the connection is what broke — from scratch, never via
	 * the stored conversation, since a conversation that would not reopen is
	 * exactly how we got here — and then re-sends the message that did not make
	 * it, so nothing has to be retyped.
	 */
	const retrySend = useCallback(
		(messageId: string) => {
			void (async () => {
				const unsent = unsentMessages[messageId];
				if (!unsent) {
					return;
				}
				if (status === 'error') {
					const connected = await connect(resumeIdRef.current);
					if (!connected) {
						return;
					}
				}
				await deliverMessage(unsent.text, unsent.id, unsent.context);
			})();
		},
		[status, unsentMessages, connect, deliverMessage]
	);

	/**
	 * The connection strip's Try again. Reconnects, then re-sends whatever did
	 * not make it out — the composer is disabled while the connection is down, so
	 * there is at most one such message and it is the last one typed.
	 */
	const retry = useCallback(() => {
		void (async () => {
			if (status === 'error') {
				const connected = await connect(resumeIdRef.current);
				if (!connected) {
					return;
				}
			}
			const ids = Object.keys(unsentMessages);
			const last = ids.length ? unsentMessages[ids[ids.length - 1]] : null;
			if (last) {
				await deliverMessage(last.text, last.id, last.context);
			}
		})();
	}, [status, unsentMessages, connect, deliverMessage]);

	/**
	 * Resolves true when the decision reached the harness.
	 *
	 * The card locks both of its buttons the moment one is pressed, so it needs
	 * to know when to unlock again — otherwise a failed decision leaves an
	 * approval that can never be answered.
	 */
	const resolveApproval = useCallback(
		async (approvalId: string, approved: boolean): Promise<boolean> => {
			try {
				await withReauth(() => actions.resolveApproval(approvalId, approved));
				return true;
			} catch (approvalError) {
				// Already settled on the harness: nothing to unlock or retry.
				if (isGone(approvalError)) {
					settleMissingApprovals((id) => id === approvalId);
					return true;
				}
				appendErrorNotice(approvalError, 'Resolving the approval failed.');
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.resolveApproval, withReauth, appendErrorNotice, settleMissingApprovals]
	);

	/**
	 * One answer for the listed approvals — a batch card's single click. Only
	 * these ids: a card that arrives after the click is not covered by it.
	 *
	 * `missing` ids are settled as "no longer pending", never reported failed:
	 * there is nothing left to answer. `failed` are the ones whose decision did
	 * not reach the harness, for the card to unlock.
	 */
	const resolveApprovals = useCallback(
		async (ids: string[], approved: boolean): Promise<AgentApprovalsResult> => {
			const result: AgentApprovalsResult = { resolved: [], missing: [], failed: [] };
			if (!ids.length) {
				return result;
			}
			let batched: AgentApprovalsResponse | null = null;
			if (actions.resolveApprovals) {
				try {
					batched = await withReauth(() => actions.resolveApprovals!(ids, approved));
				} catch (batchError) {
					if (!isMissingRoute(batchError)) {
						appendErrorNotice(batchError, 'Resolving the approvals failed.');
						result.failed = ids.slice();
						return result;
					}
				}
			}
			if (batched && !batched.ok) {
				appendNotice('error', 'Resolving the approvals failed.');
				result.failed = ids.slice();
				return result;
			}
			if (batched) {
				// The harness's own lists: an id in neither did not get an answer, so
				// its card unlocks rather than closing as "no longer pending".
				const resolved = new Set(batched.resolved);
				const missing = new Set(batched.missing);
				ids.forEach((id) =>
					(resolved.has(id)
						? result.resolved
						: missing.has(id)
						? result.missing
						: result.failed
					).push(id)
				);
			} else {
				// An older harness: one request per id, all at once. Each settles on
				// its own, so a partial failure unlocks only what did not land.
				let firstError: unknown = null;
				const outcomes = await Promise.all(
					ids.map((id) =>
						withReauth(() => actions.resolveApproval(id, approved)).then(
							() => 'resolved' as const,
							(approvalError) => {
								if (isGone(approvalError)) {
									return 'missing' as const;
								}
								firstError = firstError || approvalError;
								return 'failed' as const;
							}
						)
					)
				);
				outcomes.forEach((outcome, index) => result[outcome].push(ids[index]));
				if (firstError) {
					appendErrorNotice(firstError, 'Resolving the approvals failed.');
				}
			}
			if (result.missing.length) {
				const missing = new Set(result.missing);
				settleMissingApprovals((id) => missing.has(id));
			}
			return result;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			actions.resolveApprovals,
			actions.resolveApproval,
			withReauth,
			appendNotice,
			appendErrorNotice,
			settleMissingApprovals,
		]
	);

	/** Resolves true when the harness recorded the verification. */
	const reconcileAction = useCallback(
		async (actionId: string, outcome: AgentActionOutcome, evidence: string): Promise<boolean> => {
			if (!actions.reconcileAction) {
				return false;
			}
			try {
				const reconciled = await withReauth(() =>
					actions.reconcileAction!(actionId, outcome, evidence)
				);
				if (reconciled) {
					upsertActionReview(reconciled);
				}
				return true;
			} catch (reconcileError: any) {
				// 409: it was already answered — in another chat or tab. Not a failure;
				// re-read it so the card shows that answer.
				if (
					reconcileError &&
					reconcileError.status === 409 &&
					(await refreshActionReview(actionId))
				) {
					return true;
				}
				appendErrorNotice(reconcileError, 'Recording the verification failed.');
				return false;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			actions.reconcileAction,
			withReauth,
			upsertActionReview,
			refreshActionReview,
			appendErrorNotice,
		]
	);

	/**
	 * Resolves true when the turn is known to be stopped.
	 *
	 * It used to swallow everything and return void, so a caller could not tell
	 * a cancel that failed — the turn still running on the server — from one
	 * that worked.
	 */
	const cancelTurn = useCallback(async (): Promise<boolean> => {
		if (!conversation) {
			return true;
		}
		try {
			// Through the re-auth ladder like every other write. Swallowing a bare
			// 401 made Stop indistinguishable from "too late": the user pressed it,
			// nothing happened, and the agent carried on.
			await withReauth(() => actions.cancelTurn(conversation.id));
			return true;
		} catch (cancelError) {
			// A turn that has already ended is the one failure worth ignoring: the
			// stream's idle event is authoritative and there is nothing to tell the
			// user. Anything else means the turn may still be running.
			return cancelError && cancelError.status === 404;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conversation, actions.cancelTurn, withReauth]);

	/**
	 * Stop, as the composer's button means it.
	 *
	 * `cancelTurn` reports failure but says nothing, leaving the message to its
	 * caller. Both composers used to pass it straight through and drop the
	 * result, so a Stop that failed looked exactly like a Stop that worked while
	 * the agent carried on running.
	 */
	const stopTurn = useCallback(async () => {
		if (!(await cancelTurn())) {
			appendNotice('error', 'Could not stop the agent. It may still be running — try again.');
		}
	}, [cancelTurn, appendNotice]);

	const changeModel = useCallback(
		async (model: string) => {
			if (!conversation) {
				return;
			}
			try {
				await withReauth(() => actions.setConversationModel(conversation.id, model));
			} catch (modelError) {
				appendErrorNotice(modelError, 'Switching models failed.');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation, actions.setConversationModel, withReauth, appendErrorNotice]
	);

	const changeThinkingLevel = useCallback(
		async (thinkingLevel: string) => {
			if (!conversation) {
				return;
			}
			try {
				await withReauth(() => actions.setThinkingLevel(conversation.id, thinkingLevel));
			} catch (thinkingError) {
				appendErrorNotice(thinkingError, 'Changing the reasoning level failed.');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation, actions.setThinkingLevel, withReauth, appendErrorNotice]
	);

	/**
	 * Rejects on failure rather than posting a notice: the reason form stays open
	 * with what was typed, and says so itself.
	 */
	const sendFeedback = useCallback(
		async (messageId: string, feedback: AgentMessageFeedback) => {
			if (!conversation || !actions.sendFeedback) {
				return;
			}
			await withReauth(() => actions.sendFeedback(conversation.id, messageId, feedback));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[conversation, actions.sendFeedback, withReauth]
	);

	const uploadFile = useCallback(
		async (file: File) => {
			setUploading(true);
			try {
				const uploaded = await withReauth(() => actions.uploadFile(file));
				setPendingUploads((current) => [...current, uploaded]);
			} catch (uploadError) {
				appendErrorNotice(uploadError, 'The upload failed.');
			} finally {
				setUploading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.uploadFile, withReauth, appendErrorNotice]
	);

	const removePendingUpload = useCallback((path: string) => {
		setPendingUploads((current) => current.filter((upload) => upload.path !== path));
	}, []);

	/**
	 * Leave the conversation on screen, then enter another. Shared by New chat
	 * and opening a saved chat so both do it in the same order:
	 *
	 * 0. The click is the moment of leaving: sending stops here, though nothing
	 *    is torn down yet, so a message cannot slip into the chat being left
	 *    while the steps below are awaited.
	 * 1. `check` (optional) — read-only, so a target that will not open (404
	 *    archived, 409 open in another sign-in, 503 still being answered
	 *    elsewhere) costs nothing: the current chat is untouched.
	 * 2. Clean up, synchronously: close the stream, so the old conversation
	 *    sends nothing more; drop it, so a message has nowhere to go; clear its
	 *    transcript; record `targetId` as the conversation to resume, so the
	 *    connection strip's Try again retries the switch, not the chat left.
	 * 3. `enter` builds the next conversation. There is no going back past
	 *    step 2: a failure gets the connection strip, whose Try again runs
	 *    `connect`.
	 *
	 * A turn still running in the chat being left is **not** stopped (OPE-5443).
	 * Leaving a chat is not Stop: the harness runs the turn whether or not a
	 * stream is listening, and opening that chat again replays what it did and
	 * reports it busy if it still is — an approval it reached included, in the
	 * replay and in `hello.pending`. Stopping it here threw the person's request
	 * away just for looking at another conversation.
	 */
	const switchConversation = useCallback(
		async (targetId: string, enter: () => Promise<void>, check?: () => Promise<unknown>) => {
			if (switchingRef.current) {
				return;
			}
			switchingRef.current = true;
			setSwitching(true);
			try {
				if (check) {
					try {
						await withReauth(check);
					} catch (checkError) {
						// A dead session is already on the connection strip.
						if (!(checkError && checkError.surfaced)) {
							const reason = describeOpenFailure(checkError);
							if (options && options.onSwitchError) {
								options.onSwitchError(reason);
							} else {
								appendNotice('error', reason);
							}
						}
						return;
					}
				}
				if (unmountedRef.current) {
					return;
				}
				closeStream();
				conversationRef.current = null;
				setConversation(null);
				resumeIdRef.current = targetId;
				resetTranscript();
				setStatus('connecting');
				setErrorInfo(null);
				try {
					await withReauth(enter);
				} catch (enterError) {
					// `withReauth` has already put a dead session on the strip, and a
					// refused stream is `recoverSession`'s, already running by now.
					if (
						!unmountedRef.current &&
						!recoveringRef.current &&
						!(enterError && enterError.surfaced)
					) {
						setStatus('error');
						setErrorInfo(classifyAgentError(enterError));
					}
				}
			} finally {
				switchingRef.current = false;
				setSwitching(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[withReauth, closeStream, resetTranscript, appendNotice]
	);

	// No model: the harness picks one. Carrying this conversation's model over
	// used to pin the new chat to whatever the ladder had escalated to, since the
	// harness reads any model but the ladder's first as a choice made by hand —
	// and nothing in Oscar lets a person make that choice.
	const newConversation = useCallback(
		() =>
			switchConversation('', async () => {
				const connected = armHello();
				await startConversation();
				await connected;
			}),
		[switchConversation, startConversation, armHello]
	);

	/**
	 * Continue another of this person's conversations — a saved one from the
	 * history list included. The harness rebuilds a conversation it no longer
	 * holds from stored history and replays its turns over the stream.
	 *
	 * The check matters because a refused EventSource carries no status: without
	 * it, an archived chat or one open elsewhere would read as a dead harness and
	 * `recoverSession` would start a fresh chat.
	 */
	const openConversation = useCallback(
		(conversationId: string) => {
			if (!actions.fetchConversation || conversationId === resumeIdRef.current) {
				return Promise.resolve();
			}
			return switchConversation(
				conversationId,
				async () => {
					const resumed = armHello();
					resumeConversation(conversationId);
					if (options && options.onConversationId) {
						options.onConversationId(conversationId);
					}
					await resumed;
				},
				() => actions.fetchConversation(conversationId)
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions.fetchConversation, switchConversation, resumeConversation, armHello]
	);

	return {
		// Ready means there is a conversation to talk to and it is not being left.
		// Mid-switch that is false whatever else has set ready in the meantime (a
		// re-login does), so the composer and the rail stay shut.
		status: status === 'ready' && (switching || !conversation) ? 'connecting' : status,
		errorInfo,
		/** Convenience for callers that only want the text. */
		errorMessage: errorInfo ? errorInfo.message : '',
		unsentMessages,
		turnStartedAt,
		retry,
		retrySend,
		busy,
		entries,
		conversation,
		modelsInfo,
		pendingUploads,
		uploading,
		sendMessage,
		resolveApproval,
		resolveApprovals,
		reconcileAction,
		sendFeedback,
		cancelTurn,
		stopTurn,
		changeModel,
		changeThinkingLevel,
		uploadFile,
		removePendingUpload,
		newConversation,
		openConversation,
	};
};
